// pages/VisitNotificationDetail.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Building2,
  MessageSquare,
  Send,
  Wrench,
  Calendar,
  MapPin,
  Phone,
  Mail,
  FileText,
  Eye,
  ExternalLink,
  Edit,
  X,
  Check,
  MoreVertical,
  Trash2,
} from "lucide-react";
import apiService from "../../services/api";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

const VisitNotificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");

  // WebSocket real-time updates
  const handleRealtimeUpdate = useCallback(
    (data) => {
      console.log("📡 Received notification update:", data);

      if (data.type === "visit_notification_updated") {
        const { action, data: updateData } = data;

        if (action === "status_updated" && updateData.id === id) {
          setNotification((prev) => ({
            ...prev,
            ...updateData,
          }));
        } else if (
          action === "comment_added" &&
          updateData.notificationId === id
        ) {
          setNotification((prev) => ({
            ...prev,
            comments: [...(prev.comments || []), updateData.comment],
          }));
        }
      }
    },
    [id],
  );

  const { isConnected } = useRealtimeSync({
    events: ["visit_notification_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    fetchNotification();
    fetchUsers();
  }, [id]);

  const fetchNotification = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVisitNotification(id);
      setNotification(response.data);
    } catch (error) {
      console.error("Error fetching notification:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleStatusUpdate = async (status) => {
    try {
      setUpdatingStatus(true);
      await apiService.updateVisitNotificationStatus(id, { status });
      await fetchNotification();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResolve = async () => {
    try {
      setUpdatingStatus(true);
      await apiService.updateVisitNotificationStatus(id, {
        status: "resolved",
        resolutionNotes,
      });
      setShowResolveModal(false);
      setResolutionNotes("");
      await fetchNotification();
    } catch (error) {
      console.error("Error resolving notification:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssign = async () => {
    try {
      setUpdatingStatus(true);
      await apiService.updateVisitNotificationStatus(id, {
        status: notification.status,
        assignedTo: selectedUser,
      });
      setShowAssignModal(false);
      setSelectedUser("");
      await fetchNotification();
    } catch (error) {
      console.error("Error assigning notification:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      setSubmittingComment(true);
      await apiService.addVisitNotificationComment(id, {
        noteText: commentText,
      });
      setCommentText("");
      await fetchNotification();
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "bg-red-500/20 text-red-400 border-red-500/30",
      high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      unread: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      read: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      in_progress: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      resolved: "bg-green-500/20 text-green-400 border-green-500/30",
      dismissed: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return colors[status] || colors.unread;
  };

  const getStatusIcon = (status) => {
    const icons = {
      unread: Bell,
      read: Eye,
      in_progress: Clock,
      resolved: CheckCircle,
      dismissed: AlertCircle,
    };
    const Icon = icons[status] || Bell;
    return <Icon size={16} />;
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          Loading notification...
        </div>
      </div>
    );
  }

  if (!notification) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className="text-center">
          <AlertCircle
            className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
          />
          <h3
            className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Notification not found
          </h3>
          <button
            onClick={() => navigate("/visit-notifications")}
            className="text-blue-400 hover:text-blue-300"
          >
            Back to notifications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          >
            <ArrowLeft size={20} />
            Go Back
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1
                  className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {notification.title}
                </h1>
                {isConnected && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                    Live
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-2 ${getPriorityColor(
                    notification.priority,
                  )}`}
                >
                  <AlertCircle size={16} />
                  {notification.priority.toUpperCase()} PRIORITY
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-2 ${getStatusColor(
                    notification.status,
                  )}`}
                >
                  {getStatusIcon(notification.status)}
                  {notification.status.replace("_", " ").toUpperCase()}
                </span>

                <span
                  className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <Clock size={16} />
                  Created {formatDateTime(notification.createdAt)}
                </span>
              </div>

              <p
                className={`text-lg ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {notification.message}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {notification.status === "unread" && (
                <button
                  onClick={() => handleStatusUpdate("in_progress")}
                  disabled={updatingStatus}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Clock size={18} />
                  Start Working
                </button>
              )}

              {notification.status === "in_progress" && (
                <button
                  onClick={() => setShowResolveModal(true)}
                  disabled={updatingStatus}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <CheckCircle size={18} />
                  Mark Resolved
                </button>
              )}

              {notification.status !== "resolved" && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                >
                  <User size={18} />
                  {notification.assignedUser ? "Reassign" : "Assign"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Outstanding Work */}
            {notification.outstandingWork && (
              <div
                className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-orange-500/30" : "bg-white border border-orange-200 shadow-sm"}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="text-orange-400" size={20} />
                  <h2
                    className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Outstanding Work Details
                  </h2>
                </div>
                <div
                  className={`rounded-lg p-4 ${isDarkMode ? "bg-orange-500/10 border border-orange-500/20" : "bg-orange-50 border border-orange-100"}`}
                >
                  <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                    {notification.outstandingWork}
                  </p>
                </div>
              </div>
            )}

            {/* Visit Information */}
            {notification.visit && (
              <div
                className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2
                    className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Visit Information
                  </h2>
                  <button
                    onClick={() => navigate(`/visits/${notification.visit.id}`)}
                    className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-sm"
                  >
                    View Visit
                    <ExternalLink size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className={`flex items-start gap-3 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                  >
                    <Wrench
                      className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      size={18}
                    />
                    <div>
                      <div
                        className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Visit Number
                      </div>
                      <div
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {notification.visit.visitName}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`flex items-start gap-3 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                  >
                    <Calendar
                      className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      size={18}
                    />
                    <div>
                      <div
                        className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Visit Date
                      </div>
                      <div
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {formatDate(notification.visit.visitDate)}
                      </div>
                    </div>
                  </div>

                  {notification.visit.account && (
                    <div
                      className={`flex items-start gap-3 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                    >
                      <Building2
                        className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        size={18}
                      />
                      <div>
                        <div
                          className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          Account
                        </div>
                        <div
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {notification.visit.account.name}
                        </div>
                        {notification.visit.account.phone && (
                          <div
                            className={`text-sm flex items-center gap-1 mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <Phone size={12} />
                            {notification.visit.account.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {notification.visit.engineer && (
                    <div
                      className={`flex items-start gap-3 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                    >
                      <User
                        className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        size={18}
                      />
                      <div>
                        <div
                          className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          Engineer
                        </div>
                        <div className="flex items-center gap-2">
                          {notification.visit.engineer.profileImageUrl ? (
                            <img
                              src={notification.visit.engineer.profileImageUrl}
                              alt=""
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                            >
                              <span
                                className={`text-xs ${isDarkMode ? "text-white" : "text-gray-600"}`}
                              >
                                {notification.visit.engineer.name
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div
                            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {notification.visit.engineer.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {notification.visit.project && (
                    <div
                      className={`flex items-start gap-3 p-4 rounded-lg md:col-span-2 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                    >
                      <FileText
                        className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        size={18}
                      />
                      <div>
                        <div
                          className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          Project
                        </div>
                        <div
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {notification.visit.project.projectName}
                        </div>
                        <div
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {notification.visit.project.projectNumber}
                        </div>
                      </div>
                    </div>
                  )}

                  {notification.visit.street && (
                    <div
                      className={`flex items-start gap-3 p-4 rounded-lg md:col-span-2 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                    >
                      <MapPin
                        className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        size={18}
                      />
                      <div>
                        <div
                          className={`text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          Location
                        </div>
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {notification.visit.street}
                          {notification.visit.city &&
                            `, ${notification.visit.city}`}
                          {notification.visit.postalCode &&
                            ` ${notification.visit.postalCode}`}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Resolution Notes (if resolved) */}
            {notification.status === "resolved" &&
              notification.resolutionNotes && (
                <div
                  className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-green-500/30" : "bg-white border border-green-200 shadow-sm"}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="text-green-400" size={20} />
                    <h2
                      className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Resolution
                    </h2>
                  </div>
                  <div
                    className={`rounded-lg p-4 mb-4 ${isDarkMode ? "bg-green-500/10 border border-green-500/20" : "bg-green-50 border border-green-100"}`}
                  >
                    <p
                      className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                    >
                      {notification.resolutionNotes}
                    </p>
                  </div>
                  {notification.resolver && (
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <User size={14} />
                      Resolved by {notification.resolver.name} on{" "}
                      {formatDateTime(notification.resolvedAt)}
                    </div>
                  )}
                </div>
              )}

            {/* Comments */}
            <div
              className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
            >
              <div className="flex items-center gap-2 mb-6">
                <MessageSquare
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  size={20}
                />
                <h2
                  className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Discussion
                </h2>
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  ({notification.comments?.length || 0})
                </span>
              </div>

              {/* Comment Form */}
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      rows={3}
                      className={`w-full px-4 py-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDarkMode ? "bg-gray-900/50 border border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50" : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400"}`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingComment || !commentText.trim()}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 h-fit"
                  >
                    <Send size={18} />
                    Post
                  </button>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {notification.comments && notification.comments.length > 0 ? (
                  notification.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                    >
                      <div className="flex items-start gap-3">
                        {comment.creator?.profileImageUrl ? (
                          <img
                            src={comment.creator.profileImageUrl}
                            alt=""
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                          >
                            <span
                              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-600"}`}
                            >
                              {comment.creator?.name?.charAt(0).toUpperCase() ||
                                "?"}
                            </span>
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {comment.creator?.name || "Unknown"}
                            </span>
                            <span
                              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {formatDateTime(comment.createdAt)}
                            </span>
                          </div>
                          <p
                            className={
                              isDarkMode ? "text-gray-300" : "text-gray-600"
                            }
                          >
                            {comment.noteText}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    No comments yet. Be the first to comment!
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assignment */}
            <div
              className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Assignment
              </h3>
              {notification.assignedUser ? (
                <div className="flex items-center gap-3 mb-4">
                  {notification.assignedUser.profileImageUrl ? (
                    <img
                      src={notification.assignedUser.profileImageUrl}
                      alt=""
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                    >
                      <span
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-600"}`}
                      >
                        {notification.assignedUser.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {notification.assignedUser.name}
                    </div>
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {notification.assignedUser.email}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Not assigned
                </div>
              )}
              {notification.status !== "resolved" && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                >
                  {notification.assignedUser ? "Reassign" : "Assign to User"}
                </button>
              )}
            </div>

            {/* Timeline */}
            <div
              className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? "bg-blue-500/20 border border-blue-500/30" : "bg-blue-100 border border-blue-200"}`}
                    >
                      <Bell size={14} className="text-blue-400" />
                    </div>
                    <div
                      className={`w-px h-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} mt-2`}
                    ></div>
                  </div>
                  <div className="flex-1 pb-4">
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Created
                    </div>
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {formatDateTime(notification.createdAt)}
                    </div>
                    {notification.creator && (
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        by {notification.creator.name}
                      </div>
                    )}
                  </div>
                </div>

                {notification.status !== "unread" && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? "bg-yellow-500/20 border border-yellow-500/30" : "bg-yellow-100 border border-yellow-200"}`}
                      >
                        <Clock size={14} className="text-yellow-400" />
                      </div>
                      {notification.status === "resolved" && (
                        <div
                          className={`w-px h-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} mt-2`}
                        ></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        In Progress
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Status updated
                      </div>
                    </div>
                  </div>
                )}

                {notification.status === "resolved" && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? "bg-green-500/20 border border-green-500/30" : "bg-green-100 border border-green-200"}`}
                      >
                        <CheckCircle size={14} className="text-green-400" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Resolved
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {formatDateTime(notification.resolvedAt)}
                      </div>
                      {notification.resolver && (
                        <div
                          className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          by {notification.resolver.name}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div
              className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Details
              </h3>
              <div className="space-y-3">
                <div>
                  <div
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Type
                  </div>
                  <div className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {notification.type
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                </div>

                <div>
                  <div
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Priority
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(notification.priority)}`}
                  >
                    {notification.priority.toUpperCase()}
                  </span>
                </div>

                <div>
                  <div
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Status
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(notification.status)}`}
                  >
                    {getStatusIcon(notification.status)}
                    {notification.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>

                {notification.metadata?.visitDate && (
                  <div>
                    <div
                      className={`text-sm mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Visit Date
                    </div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {formatDate(notification.metadata.visitDate)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            {notification.status !== "resolved" && (
              <div
                className={`rounded-xl p-6 ${isDarkMode ? "bg-[#13161B] border border-gray-700/50" : "bg-white border border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  {notification.status === "unread" && (
                    <button
                      onClick={() => handleStatusUpdate("in_progress")}
                      disabled={updatingStatus}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Clock size={16} />
                      Start Working
                    </button>
                  )}

                  {notification.status === "in_progress" && (
                    <button
                      onClick={() => setShowResolveModal(true)}
                      disabled={updatingStatus}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={16} />
                      Mark Resolved
                    </button>
                  )}

                  <button
                    onClick={() => handleStatusUpdate("dismissed")}
                    disabled={updatingStatus}
                    className={`w-full px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                  >
                    <X size={16} />
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-xl p-6 max-w-md w-full ${isDarkMode ? "bg-[#13161B] border border-gray-700" : "bg-white border border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Assign Notification
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className={
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                }
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label
                className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Select User
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDarkMode ? "bg-gray-900/50 border border-gray-700/50 text-white focus:border-blue-500/50" : "bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-400"}`}
              >
                <option value="">Select a user...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedUser || updatingStatus}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-xl p-6 max-w-md w-full ${isDarkMode ? "bg-[#13161B] border border-gray-700" : "bg-white border border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Resolve Notification
              </h3>
              <button
                onClick={() => setShowResolveModal(false)}
                className={
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                }
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label
                className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Resolution Notes
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how the issue was resolved..."
                rows={4}
                className={`w-full px-4 py-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDarkMode ? "bg-gray-900/50 border border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50" : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400"}`}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowResolveModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={updatingStatus}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} />
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitNotificationDetail;
